/**
 * DecisionLogDisplay Component
 * Displays decision log entries for a parking request with formatting
 */

import LockIcon from "@mui/icons-material/Lock";
import { Box } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LogEntry, ParkingRequest } from "../types";
import { formatSizeCategory } from "../utils/formatSizeCategory";

/**
 * Format size category as short label (uses shared formatter for consistency)
 */
const formatSizeLabel = formatSizeCategory;

/**
 * Format bucket index as HH:MM time string
 */
const formatBucketTime = (bucket: number): string => {
  const hours = Math.floor(bucket / 4);
  const mins = (bucket % 4) * 15;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

interface DecisionLogDisplayProps {
  aircraft: ParkingRequest; // Note: field name stays "aircraft" for compatibility
  log: LogEntry[];
  compact?: boolean; // For tooltip vs full display
  spotIdToName?: Record<string, string>; // Mapping from spot ID to spot name
}

export const DecisionLogDisplay: React.FC<DecisionLogDisplayProps> = React.memo(
  ({ aircraft, log, compact = false, spotIdToName = {} }) => {
    const { t } = useTranslation();

    // Helper function to get spot display name
    const getSpotName = (spotId: string | undefined): string => {
      if (!spotId) return "";
      return spotIdToName[spotId] || spotId;
    };

    // Helper function to format lane name (ensure "Lane" prefix)
    const formatLaneName = (laneName: string): string => {
      // If it's just a number, prepend "Lane"
      if (/^\d+$/.test(laneName)) {
        return `${t('screens.ramp.temporalTest.decisionLogDisplay.lane')} ${laneName}`;
      }
      return laneName;
    };
    const acLogs = log.filter((e) => e.aircraftId === aircraft.id);

    if (acLogs.length === 0) {
      return (
        <Box sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          {t('screens.ramp.temporalTest.decisionLogDisplay.noEntries')}
        </Box>
      );
    }

    const placedLogs = acLogs.filter((e) => e.type === "placed");
    const rejectedLog = acLogs.find((e) => e.type === "rejected");
    const segmentedLog = acLogs.find((e) => e.type === "segmented");

    // Combine section headers and eval logs, preserving original order
    const evalAndHeaders = acLogs.filter(
      (e) => e.type === "eval" || e.type === "section-header"
    );

    return (
      <Box
        sx={{
          fontFamily: "monospace",
          fontSize: compact ? "0.85rem" : "0.875rem",
        }}
      >
        {/* Always show aircraft header, just compact the formatting in compact mode */}
        <Box
          sx={{
            fontWeight: "bold",
            mb: compact ? 0.5 : 0.5,
            color: "text.primary",
          }}
        >
          ✈️ {aircraft.tail_number} [{formatSizeLabel(aircraft.sizeCategory)}] (
          {String(Math.floor(aircraft.arrivalBucket / 4)).padStart(2, "0")}:
          {String((aircraft.arrivalBucket % 4) * 15).padStart(2, "0")} -{" "}
          {String(Math.floor(aircraft.departureBucket / 4)).padStart(2, "0")}:
          {String((aircraft.departureBucket % 4) * 15).padStart(2, "0")})
        </Box>

        {/* Evaluation logs and section headers (interleaved in order) */}
        {evalAndHeaders.map((entry, idx) => {
          // Render section headers
          if (entry.type === "section-header") {
            return (
              <Box
                key={`header-${idx}`}
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.95rem",
                  color: "info.main",
                  mt: 1,
                  mb: 1,
                  borderBottom: "2px solid",
                  borderColor: "info.main",
                  pb: 0.5,
                }}
              >
                {entry.reason}
              </Box>
            );
          }

          // Render eval logs (existing code)
          return (
            <Box
              key={idx}
              sx={{
                pl: compact ? 0 : 2,
                color: "text.secondary",
                mb: compact ? 0.5 : 0,
              }}
            >
              🅿️ {getSpotName(entry.spotId)}
              {entry.isGapSpot && (
                <span style={{ color: "#9C27B0", fontWeight: "bold" }}>
                  {" "}
                  [GAP]
                </span>
              )}
              {/* Multi-lane egress support */}
              {entry.chosenEgressOption && (
                <span style={{ opacity: 0.7 }}>
                  {" "}
                  ({formatLaneName(entry.chosenEgressOption.laneName)}
                  {entry.availableEgressOptions &&
                    entry.availableEgressOptions > 1 && (
                      <span style={{ fontSize: "0.9em" }}>
                        {" "}
                        – {entry.availableEgressOptions} lanes
                      </span>
                    )}
                  )
                </span>
              )}{" "}
              -{" "}
              {entry.timeAvailable ? (
                <span style={{ color: "green" }}>
                  ✅ Available, {entry.trappedCount} traps
                  {entry.wasteScore !== undefined && entry.wasteScore > 0 && (
                    <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                      , waste: {entry.wasteScore}
                    </span>
                  )}
                  {/* Show structural complexity */}
                  {entry.structuralComplexity !== undefined &&
                    entry.structuralComplexity > 0 && (
                      <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                        , complexity: {entry.structuralComplexity}
                      </span>
                    )}
                  {/* Show zone priority */}
                  {entry.zonePriority !== undefined && (
                    <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                      , zone: {entry.zonePriority}
                    </span>
                  )}
                  {/* Show combined score with labeled calculation breakdown */}
                  {(entry.egressScore !== undefined ||
                    entry.structuralPenalty !== undefined ||
                    entry.wastePenalty !== undefined ||
                    entry.zonePenalty !== undefined ||
                    entry.min_dwell_penalty !== undefined) && (
                    <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                      <br />
                      (score: egress({entry.trappedCount || 0}×
                      {entry.egressWeight ?? 1.0}=
                      {(entry.egressScore || 0).toFixed(1)}) + struct(
                      {entry.structuralComplexity || 0}×
                      {entry.structuralWeight ?? 0.1}=
                      {(entry.structuralPenalty || 0).toFixed(1)}) + waste(
                      {entry.wasteScore || 0}×{entry.wasteWeight ?? 1.0}=
                      {(entry.wastePenalty || 0).toFixed(1)}) + zone(
                      {entry.zonePriority !== undefined
                        ? 10 - entry.zonePriority
                        : 0}
                      ×{entry.zonePriorityWeight ?? 0}=
                      {(entry.zonePenalty || 0).toFixed(1)})
                      {entry.min_dwell_penalty !== undefined && (
                        <> + minDwell({entry.min_dwell_penalty.toFixed(1)})</>
                      )}
                      {entry.overflowPenalty !== undefined &&
                        entry.overflowPenalty > 0 && (
                          <> + overflow({entry.overflowPenalty.toFixed(1)})</>
                        )}{" "}
                      ={" "}
                      {(
                        (entry.egressScore || 0) +
                        (entry.structuralPenalty || 0) +
                        (entry.wastePenalty || 0) +
                        (entry.zonePenalty || 0) +
                        (entry.min_dwell_penalty || 0) +
                        (entry.overflowPenalty || 0)
                      ).toFixed(1)}
                      )
                    </span>
                  )}
                  {entry.blockedAircraft &&
                    entry.blockedAircraft.length > 0 && (
                      <span style={{ opacity: 0.8, fontSize: "0.85em" }}>
                        {" "}
                        [blocks: {entry.blockedAircraft.join(", ")}]
                      </span>
                    )}
                </span>
              ) : (
                <span style={{ color: "red" }}>{t('screens.ramp.temporalTest.decisionLogDisplay.timeConflict')}</span>
              )}
              {/* Show blocking details breakdown */}
              {entry.blockingDetails && entry.blockingDetails.length > 0 && (
                <Box
                  sx={{
                    pl: compact ? 1 : 2,
                    fontSize: "0.8em",
                    fontWeight: "normal",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                >
                  {entry.blockingDetails.map((detail, idx) => (
                    <Box key={idx} sx={{ fontFamily: "monospace" }}>
                      {t('screens.ramp.temporalTest.decisionLogDisplay.blocks')} {getSpotName(detail.spotId)}
                      {detail.occupiedBy && ` (${detail.occupiedBy})`}
                      {" - "}
                      <span
                        style={{
                          color: detail.isTrapped ? "#ff9800" : "#4caf50",
                        }}
                      >
                        {detail.reason}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
              {/* Show gap spot dependency status */}
              {entry.isGapSpot && entry.gapRequiredSpots && (
                <Box
                  sx={{
                    pl: compact ? 1 : 2,
                    fontSize: "0.85em",
                    fontWeight: "normal",
                    color: "text.secondary",
                    mt: 0.25,
                  }}
                >
                  {entry.gapDependenciesMet === entry.gapDependenciesTotal ? (
                    <span style={{ color: "green" }}>
                      {t('screens.ramp.temporalTest.decisionLogDisplay.deps')} {entry.gapRequiredSpots.join(", ")} (
                      {entry.gapDependenciesMet}/{entry.gapDependenciesTotal})
                    </span>
                  ) : (
                    <span style={{ color: "orange" }}>
                      {t('screens.ramp.temporalTest.decisionLogDisplay.depsWarning')} {entry.gapRequiredSpots.join(", ")} (
                      {entry.gapDependenciesMet}/{entry.gapDependenciesTotal})
                    </span>
                  )}
                  {entry.gapCollision && (
                    <span style={{ color: "red" }}>{t('screens.ramp.temporalTest.decisionLogDisplay.collision')}</span>
                  )}
                </Box>
              )}
              {/* Show gap spot reason if provided */}
              {entry.isGapSpot && entry.reason && !entry.gapRequiredSpots && (
                <Box
                  sx={{
                    pl: compact ? 1 : 2,
                    fontSize: "0.85em",
                    fontWeight: "normal",
                    color: "info.main",
                    mt: 0.25,
                  }}
                >
                  ℹ️ {entry.reason}
                </Box>
              )}
              {/* Show mask debug info if validation mode was used (Phase 9) */}
              {entry.maskDebug && (
                <Box
                  sx={{
                    pl: compact ? 1 : 2,
                    fontSize: "0.8em",
                    fontWeight: "normal",
                    color:
                      entry.maskDebug.matched === false
                        ? "error.main"
                        : "info.main",
                    mt: 0.25,
                  }}
                >
                  {entry.maskDebug.usedMasks ? (
                    <span>
                      🔧 Mask: {entry.maskDebug.maskResult}
                      {entry.maskDebug.loopResult !== undefined && (
                        <span>
                          {" "}
                          | Loop: {entry.maskDebug.loopResult}
                          {entry.maskDebug.matched === false && (
                            <span
                              style={{ color: "#f44336", fontWeight: "bold" }}
                            >
                              {" "}
                              ❌ MISMATCH
                            </span>
                          )}
                          {entry.maskDebug.matched === true && (
                            <span style={{ color: "#4caf50" }}> ✓</span>
                          )}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>🔧 Loop fallback (no masks)</span>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
        {placedLogs.map((placedLog, idx) => (
          <Box
            key={`placed-${idx}`}
            sx={{
              pl: compact ? 0 : 2,
              color: "success.main",
              fontWeight: "bold",
              mb: compact ? 0.5 : 0,
            }}
          >
            ✅ Placed at {getSpotName(placedLog.spotId)}
            {placedLog.isGapSpot && (
              <span style={{ color: "#9C27B0" }}> [GAP]</span>
            )}
            {placedLog.lockConstraint && placedLog.lockConstraint.isLocked && (
              <span
                style={{
                  color: "#FF6F00",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {" "}
                <LockIcon sx={{ fontSize: "0.9em" }} />
                [LOCKED]
                {placedLog.lockConstraint.reason &&
                  ` (${placedLog.lockConstraint.reason})`}
              </span>
            )}
            {/* Multi-lane egress support */}
            {placedLog.chosenEgressOption && (
              <span style={{ opacity: 0.8 }}>
                {" "}
                ({formatLaneName(placedLog.chosenEgressOption.laneName)}
                {placedLog.availableEgressOptions &&
                  placedLog.availableEgressOptions > 1 && (
                    <span style={{ fontSize: "0.9em" }}>
                      {" "}
                      – {placedLog.availableEgressOptions} lanes
                    </span>
                  )}
                )
              </span>
            )}
            {(placedLog.trappedCount !== undefined ||
              placedLog.structuralComplexity !== undefined ||
              placedLog.wasteScore !== undefined ||
              placedLog.zonePriority !== undefined) && (
              <span>
                {" "}
                (
                {placedLog.trappedCount !== undefined &&
                  `${placedLog.trappedCount} traps`}
                {placedLog.trappedCount !== undefined &&
                  (placedLog.structuralComplexity !== undefined ||
                    placedLog.wasteScore !== undefined ||
                    placedLog.zonePriority !== undefined) &&
                  ", "}
                {placedLog.structuralComplexity !== undefined &&
                  placedLog.structuralComplexity > 0 &&
                  `complexity: ${placedLog.structuralComplexity}`}
                {placedLog.structuralComplexity !== undefined &&
                  placedLog.structuralComplexity > 0 &&
                  (placedLog.wasteScore !== undefined ||
                    placedLog.zonePriority !== undefined) &&
                  ", "}
                {placedLog.wasteScore !== undefined &&
                  placedLog.wasteScore > 0 &&
                  `waste: ${placedLog.wasteScore}`}
                {placedLog.wasteScore !== undefined &&
                  placedLog.wasteScore > 0 &&
                  placedLog.zonePriority !== undefined &&
                  ", "}
                {placedLog.zonePriority !== undefined &&
                  `zone: ${placedLog.zonePriority}`}
                {(placedLog.egressScore !== undefined ||
                  placedLog.structuralPenalty !== undefined ||
                  placedLog.wastePenalty !== undefined ||
                  placedLog.zonePenalty !== undefined) && (
                  <span
                    style={{
                      opacity: 0.7,
                      fontSize: "0.85em",
                      fontWeight: "normal",
                    }}
                  >
                    <br />
                    score: egress({placedLog.trappedCount || 0}×
                    {placedLog.egressWeight ?? 1.0}=
                    {(placedLog.egressScore || 0).toFixed(1)}) + struct(
                    {placedLog.structuralComplexity || 0}×
                    {placedLog.structuralWeight ?? 0.1}=
                    {(placedLog.structuralPenalty || 0).toFixed(1)}) + waste(
                    {placedLog.wasteScore || 0}×{placedLog.wasteWeight ?? 1.0}=
                    {(placedLog.wastePenalty || 0).toFixed(1)}) + zone(
                    {placedLog.zonePriority !== undefined
                      ? 10 - placedLog.zonePriority
                      : 0}
                    ×{placedLog.zonePriorityWeight ?? 0}=
                    {(placedLog.zonePenalty || 0).toFixed(1)})
                    {placedLog.min_dwell_penalty !== undefined && (
                      <> + minDwell({placedLog.min_dwell_penalty.toFixed(1)})</>
                    )}
                    {placedLog.overflowPenalty !== undefined &&
                      placedLog.overflowPenalty > 0 && (
                        <> + overflow({placedLog.overflowPenalty.toFixed(1)})</>
                      )}{" "}
                    ={" "}
                    {(
                      (placedLog.egressScore || 0) +
                      (placedLog.structuralPenalty || 0) +
                      (placedLog.wastePenalty || 0) +
                      (placedLog.zonePenalty || 0) +
                      (placedLog.min_dwell_penalty || 0) +
                      (placedLog.overflowPenalty || 0)
                    ).toFixed(1)}
                  </span>
                )}
                )
              </span>
            )}
            {placedLog.blockedAircraft &&
              placedLog.blockedAircraft.length > 0 && (
                <span
                  style={{
                    opacity: 0.8,
                    fontSize: "0.85em",
                    fontWeight: "normal",
                  }}
                >
                  {" "}
                  [blocks: {placedLog.blockedAircraft.join(", ")}]
                </span>
              )}
            {placedLog.reason && (
              <Box
                sx={{
                  pl: compact ? 1 : 2,
                  fontSize: "0.85em",
                  fontWeight: "normal",
                  color: "info.main",
                }}
              >
                ℹ️ {placedLog.reason}
              </Box>
            )}
          </Box>
        ))}
        {segmentedLog && (
          <Box
            sx={{
              pl: compact ? 0 : 2,
              color: "info.main",
              fontWeight: "bold",
              mb: compact ? 0.5 : 0,
            }}
          >
            🔀 Segmented: {segmentedLog.reason}
          </Box>
        )}
        {rejectedLog && (
          <Box
            sx={{
              pl: compact ? 0 : 2,
              color: "error.main",
              fontWeight: "bold",
              mb: compact ? 0.5 : 0,
            }}
          >
            ❌ {rejectedLog.reason}
            {rejectedLog.lockConstraint &&
              !rejectedLog.lockConstraint.lockValidated && (
                <span
                  style={{
                    color: "#FF6F00",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {" "}
                  <LockIcon sx={{ fontSize: "0.9em" }} />
                  [LOCK FAILED]
                  {rejectedLog.lockConstraint.reason &&
                    ` (${rejectedLog.lockConstraint.reason})`}
                </span>
              )}
            {/* COMPACT SUMMARY: Show primary blocker and top suggestion only */}
            {rejectedLog.suggestionDetails && (
              <Box
                sx={{
                  pl: compact ? 1 : 2,
                  fontSize: "0.85em",
                  mt: 0.5,
                  fontFamily: "monospace",
                }}
              >
                {/* Primary Blocker - Highly visible */}
                <Box sx={{ color: "error.main", fontWeight: "bold", mb: 0.25 }}>
                  {t('screens.ramp.temporalTest.decisionLogDisplay.primaryBlocker')}{" "}
                  {rejectedLog.suggestionDetails.primaryBlocker === "size"
                    ? t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.sizeIncompatible')
                    : rejectedLog.suggestionDetails.primaryBlocker === "time"
                    ? t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.timeConflicts')
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "duration"
                    ? t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.durationExceeded')
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "disabled"
                    ? t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.spotsDisabled')
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "geometry"
                    ? t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.geometryCollision')
                    : t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.unknown')}{" "}
                  (
                  {rejectedLog.suggestionDetails.primaryBlocker === "size"
                    ? rejectedLog.filterBreakdown?.sizeIncompatible
                    : rejectedLog.suggestionDetails.primaryBlocker === "time"
                    ? rejectedLog.filterBreakdown?.timeConflict
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "duration"
                    ? rejectedLog.filterBreakdown?.durationExceeded
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "disabled"
                    ? rejectedLog.filterBreakdown?.disabled
                    : rejectedLog.suggestionDetails.primaryBlocker ===
                      "geometry"
                    ? rejectedLog.filterBreakdown?.geometryCollision
                    : 0}{" "}
                  {t('screens.ramp.temporalTest.decisionLogDisplay.of')} {rejectedLog.filterBreakdown?.total} {t('screens.ramp.temporalTest.decisionLogDisplay.spots')})
                </Box>

                {/* Top Suggestion - Single line, actionable */}
                {rejectedLog.suggestionDetails.suggestions[0] && (
                  <Box
                    sx={{ color: "primary.main", fontWeight: "bold", ml: 1 }}
                  >
                    → {rejectedLog.suggestionDetails.suggestions[0].title}
                    {rejectedLog.suggestionDetails.suggestions[0].impact && (
                      <span style={{ color: "#4caf50" }}>
                        {" "}
                        ({rejectedLog.suggestionDetails.suggestions[0].impact})
                      </span>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {/* DETAILED BREAKDOWN: Compact funnel view - text only, no bars */}
            {rejectedLog.filterBreakdown && (
              <Box
                sx={{
                  pl: compact ? 1 : 2,
                  fontSize: "0.75em",
                  fontWeight: "normal",
                  color: "text.secondary",
                  mt: 0.75,
                  fontFamily: "monospace",
                  opacity: 0.8,
                }}
              >
                <Box
                  sx={{
                    fontWeight: "bold",
                    mb: 0.15,
                    color: "text.primary",
                    fontSize: "0.95em",
                  }}
                >
                  {t('screens.ramp.temporalTest.decisionLogDisplay.breakdown')}
                </Box>
                {/* Build funnel flow - calculate remaining after each filter */}
                {(() => {
                  const total = rejectedLog.filterBreakdown.total;
                  let remaining = total;
                  const filters = [];

                  // Determine primary blocker for highlighting
                  const primaryBlockerCount = Math.max(
                    rejectedLog.filterBreakdown.disabled,
                    rejectedLog.filterBreakdown.sizeIncompatible,
                    rejectedLog.filterBreakdown.timeConflict,
                    rejectedLog.filterBreakdown.durationExceeded,
                    rejectedLog.filterBreakdown.geometryCollision
                  );

                  if (rejectedLog.filterBreakdown.disabled > 0) {
                    const isPrimary =
                      rejectedLog.filterBreakdown.disabled ===
                      primaryBlockerCount;
                    filters.push({
                      label: t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.spotsDisabled'),
                      eliminated: rejectedLog.filterBreakdown.disabled,
                      remaining:
                        remaining - rejectedLog.filterBreakdown.disabled,
                      isPrimary,
                    });
                    remaining -= rejectedLog.filterBreakdown.disabled;
                  }

                  if (rejectedLog.filterBreakdown.sizeIncompatible > 0) {
                    const isPrimary =
                      rejectedLog.filterBreakdown.sizeIncompatible ===
                      primaryBlockerCount;
                    filters.push({
                      label: t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.sizeIncompatible'),
                      eliminated: rejectedLog.filterBreakdown.sizeIncompatible,
                      remaining:
                        remaining -
                        rejectedLog.filterBreakdown.sizeIncompatible,
                      isPrimary,
                    });
                    remaining -= rejectedLog.filterBreakdown.sizeIncompatible;
                  }

                  if (rejectedLog.filterBreakdown.timeConflict > 0) {
                    const isPrimary =
                      rejectedLog.filterBreakdown.timeConflict ===
                      primaryBlockerCount;
                    filters.push({
                      label: t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.timeConflicts'),
                      eliminated: rejectedLog.filterBreakdown.timeConflict,
                      remaining:
                        remaining - rejectedLog.filterBreakdown.timeConflict,
                      isPrimary,
                    });
                    remaining -= rejectedLog.filterBreakdown.timeConflict;
                  }

                  if (rejectedLog.filterBreakdown.durationExceeded > 0) {
                    const isPrimary =
                      rejectedLog.filterBreakdown.durationExceeded ===
                      primaryBlockerCount;
                    filters.push({
                      label: t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.durationExceeded'),
                      eliminated: rejectedLog.filterBreakdown.durationExceeded,
                      remaining:
                        remaining -
                        rejectedLog.filterBreakdown.durationExceeded,
                      isPrimary,
                    });
                    remaining -= rejectedLog.filterBreakdown.durationExceeded;
                  }

                  if (rejectedLog.filterBreakdown.geometryCollision > 0) {
                    const isPrimary =
                      rejectedLog.filterBreakdown.geometryCollision ===
                      primaryBlockerCount;
                    filters.push({
                      label: t('screens.ramp.temporalTest.decisionLogDisplay.rejectionReasons.geometryCollision'),
                      eliminated: rejectedLog.filterBreakdown.geometryCollision,
                      remaining:
                        remaining -
                        rejectedLog.filterBreakdown.geometryCollision,
                      isPrimary,
                    });
                    remaining -= rejectedLog.filterBreakdown.geometryCollision;
                  }

                  return (
                    <Box sx={{ ml: 1 }}>
                      {/* Simplified text-only funnel */}
                      {filters.map((filter, idx) => (
                        <Box
                          key={idx}
                          sx={{ fontSize: "0.95em", lineHeight: 1.3 }}
                        >
                          -{filter.eliminated} {filter.label.toLowerCase()} (
                          {Math.round((filter.eliminated / total) * 100)}%)
                          {filter.isPrimary && (
                            <span
                              style={{ color: "#f44336", marginLeft: "4px" }}
                            >
                              {t('screens.ramp.temporalTest.decisionLogDisplay.primary')}
                            </span>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
            {/* DETAILED ANALYSIS: Only show primary blocker's analysis */}
            {rejectedLog.suggestionDetails && (
              <Box
                sx={{
                  pl: compact ? 1 : 2,
                  fontSize: "0.75em",
                  fontWeight: "normal",
                  color: "text.secondary",
                  mt: 0.5,
                  fontFamily: "monospace",
                  opacity: 0.85,
                }}
              >
                {/* Size Analysis - only if primary blocker */}
                {rejectedLog.suggestionDetails.sizeAnalysis &&
                  rejectedLog.suggestionDetails.primaryBlocker === "size" && (
                    <Box sx={{ mb: 0.75 }}>
                      <Box
                        sx={{
                          fontWeight: "bold",
                          mb: 0.25,
                          color: "text.primary",
                        }}
                      >
                        {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.title')}
                      </Box>
                      <Box sx={{ ml: 1 }}>
                        {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.aircraft')}{" "}
                        {
                          rejectedLog.suggestionDetails.sizeAnalysis
                            .aircraftSize
                        }
                        {rejectedLog.suggestionDetails.sizeAnalysis
                          .aircraftWingspan &&
                          rejectedLog.suggestionDetails.sizeAnalysis
                            .aircraftLength && (
                            <span style={{ opacity: 0.7 }}>
                              {" "}
                              {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.wingspan')}{" "}
                              {
                                rejectedLog.suggestionDetails.sizeAnalysis
                                  .aircraftWingspan
                              }
                              {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.length')}{" "}
                              {
                                rejectedLog.suggestionDetails.sizeAnalysis
                                  .aircraftLength
                              }
                              ft)
                            </span>
                          )}
                      </Box>
                      <Box sx={{ ml: 1 }}>
                        {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.spotsBySize')}{" "}
                        {Object.entries(
                          rejectedLog.suggestionDetails.sizeAnalysis.spotsBySize
                        )
                          .filter(([_, count]) => count > 0)
                          .map(([size, count]) => `${count} ${size}`)
                          .join(", ") || t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.none')}
                      </Box>
                      <Box
                        sx={{ ml: 1, color: "error.main", fontWeight: "bold" }}
                      >
                        {t('screens.ramp.temporalTest.decisionLogDisplay.sizeAnalysis.spotsAllowing')}{" "}
                        {
                          rejectedLog.suggestionDetails.sizeAnalysis
                            .aircraftSize
                        }
                        :{" "}
                        {
                          rejectedLog.suggestionDetails.sizeAnalysis
                            .currentAvailable
                        }{" "}
                        {t('screens.ramp.temporalTest.decisionLogDisplay.of')} {rejectedLog.filterBreakdown?.total || 0}
                      </Box>
                    </Box>
                  )}

                {/* Time Analysis - only if primary blocker */}
                {rejectedLog.suggestionDetails.timeAnalysis &&
                  rejectedLog.suggestionDetails.primaryBlocker === "time" && (
                    <Box sx={{ mb: 0.75 }}>
                      <Box
                        sx={{
                          fontWeight: "bold",
                          mb: 0.25,
                          color: "text.primary",
                        }}
                      >
                        {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.title')}
                      </Box>
                      <Box sx={{ ml: 1 }}>
                        {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.aircraftDwell')}{" "}
                        {formatBucketTime(
                          rejectedLog.suggestionDetails.timeAnalysis
                            .aircraftArrivalBucket
                        )}{" "}
                        -{" "}
                        {formatBucketTime(
                          rejectedLog.suggestionDetails.timeAnalysis
                            .aircraftDepartureBucket
                        )}{" "}
                        (
                        {
                          rejectedLog.suggestionDetails.timeAnalysis
                            .dwellDurationMinutes
                        }{" "}
                        {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.minutes')}
                      </Box>
                      {rejectedLog.suggestionDetails.timeAnalysis
                        .peakCongestionStart !== undefined &&
                        rejectedLog.suggestionDetails.timeAnalysis
                          .peakCongestionEnd !== undefined && (
                          <>
                            <Box
                              sx={{
                                ml: 1,
                                color: "warning.main",
                                fontWeight: "bold",
                              }}
                            >
                              {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.peakCongestion')}{" "}
                              {formatBucketTime(
                                rejectedLog.suggestionDetails.timeAnalysis
                                  .peakCongestionStart
                              )}{" "}
                              -{" "}
                              {formatBucketTime(
                                rejectedLog.suggestionDetails.timeAnalysis
                                  .peakCongestionEnd
                              )}
                              {rejectedLog.suggestionDetails.timeAnalysis
                                .peakOccupiedSpots !== undefined && (
                                <span style={{ opacity: 0.8 }}>
                                  {" "}
                                  (
                                  {
                                    rejectedLog.suggestionDetails.timeAnalysis
                                      .peakOccupiedSpots
                                  }{" "}
                                  {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.spotsOccupied')}
                                </span>
                              )}
                            </Box>
                            {rejectedLog.suggestionDetails.timeAnalysis
                              .conflictWindowBuckets !== undefined && (
                              <Box sx={{ ml: 1 }}>
                                {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.conflictWindow')}{" "}
                                {rejectedLog.suggestionDetails.timeAnalysis
                                  .conflictWindowBuckets * 15}{" "}
                                {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.minutesSuffix')}
                              </Box>
                            )}
                          </>
                        )}
                      <Box
                        sx={{ ml: 1, color: "error.main", fontWeight: "bold" }}
                      >
                        {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.timeConflicts')}{" "}
                        {rejectedLog.filterBreakdown?.timeConflict || 0} {t('screens.ramp.temporalTest.decisionLogDisplay.of')}{" "}
                        {rejectedLog.filterBreakdown?.total || 0} {t('screens.ramp.temporalTest.decisionLogDisplay.timeAnalysis.spotsOccupiedSuffix')}
                      </Box>
                    </Box>
                  )}

                {/* Duration Analysis - only if primary blocker */}
                {rejectedLog.suggestionDetails.durationAnalysis &&
                  rejectedLog.suggestionDetails.primaryBlocker ===
                    "duration" && (
                    <Box sx={{ mb: 0.75 }}>
                      <Box
                        sx={{
                          fontWeight: "bold",
                          mb: 0.25,
                          color: "text.primary",
                        }}
                      >
                        {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.title')}
                      </Box>
                      <Box sx={{ ml: 1 }}>
                        {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.requiredDwell')}{" "}
                        {
                          rejectedLog.suggestionDetails.durationAnalysis
                            .requiredDwellMinutes
                        }{" "}
                        {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.minutes')}
                      </Box>
                      {rejectedLog.suggestionDetails.durationAnalysis
                        .mostRestrictiveLimit && (
                        <>
                          <Box sx={{ ml: 1 }}>
                            {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.spotLimit')}{" "}
                            {
                              rejectedLog.suggestionDetails.durationAnalysis
                                .mostRestrictiveLimit
                            }{" "}
                            {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.minutes')}
                          </Box>
                          {rejectedLog.suggestionDetails.durationAnalysis
                            .excessDuration && (
                            <Box
                              sx={{
                                ml: 1,
                                color: "error.main",
                                fontWeight: "bold",
                              }}
                            >
                              {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.exceedsBy')}{" "}
                              {
                                rejectedLog.suggestionDetails.durationAnalysis
                                  .excessDuration
                              }{" "}
                              {t('screens.ramp.temporalTest.decisionLogDisplay.durationAnalysis.minutes')}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}

                {/* Near-Miss Analysis (Phase 4) - Compact: Just show first near-miss if exists */}
                {rejectedLog.suggestionDetails.nearMisses &&
                  rejectedLog.suggestionDetails.nearMisses.length > 0 && (
                    <Box sx={{ mb: 0.5, fontSize: "0.95em" }}>
                      <Box sx={{ color: "warning.main", fontWeight: "bold" }}>
                        {t('screens.ramp.temporalTest.decisionLogDisplay.quickFix')}{" "}
                        {getSpotName(
                          rejectedLog.suggestionDetails.nearMisses[0].spotId
                        )}{" "}
                        <span style={{ opacity: 0.8 }}>
                          (
                          {
                            rejectedLog.suggestionDetails.nearMisses[0]
                              .specificIssue
                          }
                          )
                        </span>
                      </Box>
                    </Box>
                  )}

                {/* Actionable Suggestions - Show only top 2 */}
                {rejectedLog.suggestionDetails.suggestions.length > 1 && (
                  <Box sx={{ mb: 0.25, mt: 0.5 }}>
                    <Box
                      sx={{
                        fontWeight: "bold",
                        mb: 0.15,
                        color: "text.primary",
                        fontSize: "0.95em",
                      }}
                    >
                      {t('screens.ramp.temporalTest.decisionLogDisplay.otherOptions')}
                    </Box>
                    {rejectedLog.suggestionDetails.suggestions
                      .slice(1, 2)
                      .map((suggestion, idx) => (
                        <Box
                          key={idx}
                          sx={{ ml: 1, fontSize: "0.95em", opacity: 0.9 }}
                        >
                          • {suggestion.title}
                          {suggestion.impact && (
                            <span
                              style={{ color: "#4caf50", marginLeft: "4px" }}
                            >
                              ({suggestion.impact})
                            </span>
                          )}
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            )}
            {/* Show size category details for size mismatches
              WHY: Makes it clear why aircraft was rejected due to size restrictions
              EXAMPLE: "Aircraft size: L (no spots allow large aircraft)" */}
            {rejectedLog.sizeMismatch &&
              rejectedLog.aircraftSize &&
              !rejectedLog.filterBreakdown && (
                <Box
                  sx={{
                    pl: compact ? 1 : 2,
                    fontSize: "0.85em",
                    fontWeight: "normal",
                    color: "error.dark",
                    opacity: 0.9,
                  }}
                >
                  {t('screens.ramp.temporalTest.decisionLogDisplay.aircraftSize')} {formatSizeLabel(rejectedLog.aircraftSize)}{" "}
                  ({rejectedLog.aircraftSize})
                </Box>
              )}
            {rejectedLog.blockedAircraft &&
              rejectedLog.blockedAircraft.length > 0 && (
                <span
                  style={{
                    opacity: 0.8,
                    fontSize: "0.85em",
                    fontWeight: "normal",
                  }}
                >
                  {" "}
                  [would block: {rejectedLog.blockedAircraft.join(", ")}]
                </span>
              )}
          </Box>
        )}
      </Box>
    );
  }
);
